import React from 'react';
import { X, Send } from 'lucide-react';
import safeToast from '@/utils/safeToast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // Card owner context
  handle: string;
  username: string;
  ownerName?: string;
  ownerTitle?: string;
  ownerPhone?: string;
  ownerWebsite?: string;
};

export const EmailSignatureModal: React.FC<Props> = ({ isOpen, onClose, handle, username, ownerName, ownerTitle, ownerPhone, ownerWebsite }) => {
  const [to, setTo] = React.useState('');
  const [subject, setSubject] = React.useState('Open my digital card');
  const [sending, setSending] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setTo('');
      setSubject(`Open ${ownerName || 'my'} Tcard`);
      setSending(false);
    }
  }, [isOpen, ownerName]);

  if (!isOpen) return null;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = to.trim();
    if (!email) { safeToast('error', 'Enter a recipient email'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { safeToast('error', 'Invalid email address'); return; }
    setSending(true);
    try {
      const res = await fetch('/api/email/send-signature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          subject: subject || `Open ${ownerName || 'my'} Tcard`,
          handle,
          username,
          fullName: ownerName,
          title: ownerTitle,
          phone: ownerPhone,
          website: ownerWebsite,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || 'Send failed');
      }
      safeToast('success', 'Email sent!');
      onClose();
    } catch (e: any) {
      console.error(e);
      safeToast('error', e?.message || 'Failed to send email');
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md relative shadow-xl">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close">
          <X className="w-5 h-5 text-gray-500" />
        </button>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Send Email</h3>
        <p className="text-gray-600 mb-4 text-sm">Send a signature email with inline preview of the card.</p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
            <input type="email" value={to} onChange={(e)=>setTo(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="recipient@example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input type="text" value={subject} onChange={(e)=>setSubject(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={sending} className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center disabled:opacity-50">
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Sending…' : 'Send Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailSignatureModal;
